/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRef, useState } from 'react';
import SwiperCore, { Navigation, Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    LeftArrow,
    LikeIcon,
    RightArrow,
    RightArrowIcon,
} from '../../assets/icons';

import { Link, useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import Button from '../../UI/Button/Button';
import photoSrc from '../../assets/images/EventImage.png';
import { dateConverter } from '../../helpers/dateConverter';
import { useAppSelector } from '../../hooks/reduxHooks/useAppSelector';
import { useChangeLike } from '../../hooks/useChangeLike';
import { PATH_DASHBOARD } from '../../routes/RouterConfig';
import { IEventCard } from '../../types/types';
import { LocationMark } from '../LocationMark/LocationMark';
import { Tag } from '../Tag/Tag';
import styles from './EventsSlider.module.scss';
import './styles.css';

interface EventsSliderProps {
    eventData: null | IEventCard[];
}

SwiperCore.use([Navigation]);

const EventsSlider = ({ eventData }: EventsSliderProps) => {
    const swiperRef = useRef<SwiperType>();
    // карточки с фото на всю ширину: на экране две, одна под другой.
    // Пары собираем сами: модуль Grid раскладывает слайды по строкам,
    // и вторая по дате карточка уезжала на следующий экран
    const pages = eventData
        ? Array.from({ length: Math.ceil(eventData.length / 2) }, (_, i) =>
              eventData.slice(i * 2, i * 2 + 2),
          )
        : [];
    const showNavButtons = pages.length > 1;

    return (
        <div className={styles.EventsSlider}>
            <h3 className={styles.EventsSlider__header}>
                Ближайшие мероприятия{' '}
            </h3>
            {eventData && (
                <Swiper
                    onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    slidesPerView={1}
                    spaceBetween={0}
                    modules={[Navigation]}
                    className="mySwiper"
                >
                    {pages.map((page, index) => (
                        <SwiperSlide key={index}>
                            <div className={styles.EventsSlider__page}>
                                {page.map((event: IEventCard) => (
                                    <EventSlide
                                        id={String(event.id)}
                                        // у новости и мероприятия id может совпасть
                                        key={`${event.kind}-${event.id}`}
                                        kind={event.kind}
                                        title={event.title}
                                        datetime={event.datetime}
                                        format={event.format}
                                        address={event.address}
                                        image={event.image}
                                        in_user_favorite_events={
                                            event.in_user_favorite_events
                                        }
                                    />
                                ))}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
            {/* стрелки и ссылка на архив одной строкой под карточками */}
            <div className={styles.EventsSlider__footer}>
                {showNavButtons ? (
                    <div className={styles.EventsSlider__buttons}>
                        <button
                            className={styles.EventsSlider__button}
                            onClick={() => swiperRef.current?.slidePrev()}
                        >
                            <LeftArrow />
                        </button>
                        <button
                            className={styles.EventsSlider__button}
                            onClick={() => swiperRef.current?.slideNext()}
                        >
                            <RightArrow />
                        </button>
                    </div>
                ) : null}
                <div className={styles.EventsSlider__link}>
                    <Link
                        to={PATH_DASHBOARD.eventsArchive}
                        className={styles.EventsSlider__link_text}
                    >
                        Архив событий
                    </Link>
                    <RightArrowIcon />
                </div>
            </div>
        </div>
    );
};

interface EventSlideProps {
    id: string;
    kind?: IEventCard['kind'];
    title: string;
    datetime: string;
    format: string;
    address: string;
    image: string | null;
    in_user_favorite_events: boolean;
}

const EventSlide = ({
    title,
    datetime,
    format,
    address,
    image,
    id,
    kind,
    in_user_favorite_events,
}: EventSlideProps) => {
    const [isCardLiked, setIsCardLiked] = useState(in_user_favorite_events);
    const { isLogin } = useAppSelector((state) => state.authReducer);

    // среди ближайших мероприятий идут и анонсы из новостей с галочкой
    // «Это мероприятие»: ведут на новость, без избранного,
    // формат и место у них необязательны
    const isNews = kind === 'news';
    const navigate = useNavigate();
    const navigateToCurrentEvent = () => {
        navigate(isNews ? `/news/${id}` : `/events/${id}`);
    };
    const { likeEvent, cancelLikeEvent } = useChangeLike(Number(id));

    return (
        <div className={styles.EventSlide}>
            <img
                onClick={navigateToCurrentEvent}
                className={styles.EventSlide__image}
                src={image ? image : photoSrc}
                alt="событие"
            />
            <div className={styles.EventSlide__body}>
                <div className={styles.EventSlide__top}>
                    {format ? (
                        <Tag
                            type="format"
                            content={format === 'ON' ? 'Онлайн' : 'Офлайн'}
                        />
                    ) : null}
                    <Tag
                        type="date"
                        content={dateConverter(new Date(datetime))}
                    />
                    {!isNews ? (
                        <LikeIcon
                            onClick={() => {
                                if (!isLogin) {
                                    return navigate({ search: '?login=true' });
                                }
                                isCardLiked ? cancelLikeEvent() : likeEvent();
                                setIsCardLiked(!isCardLiked);
                            }}
                            className={
                                isCardLiked
                                    ? styles.EventSlide__like_active
                                    : styles.EventSlide__like
                            }
                        />
                    ) : null}
                </div>
                <h3
                    onClick={navigateToCurrentEvent}
                    className={styles.EventSlide__header}
                >
                    {title}
                </h3>
                <div className={styles.EventSlide__bottom}>
                    <Button
                        className={styles.EventSlide__moreinfo}
                        onClick={navigateToCurrentEvent}
                    >
                        <span className={styles.EventSlide__moreinfo_text}>
                            Подробнее
                        </span>
                        <div className={styles.EventSlide__moreinfo_icon}>
                            <RightArrowIcon />
                        </div>
                    </Button>
                    {address ? <LocationMark text={address} /> : null}
                </div>
            </div>
        </div>
    );
};

export default EventsSlider;
